<template>
  <v-form
    ref="vFormRef"
    :value="value"
    :lazy-validation="lazyValidation"
    v-bind="$attrs"
    @submit="submit($event)"
    @input="emit('update:value', $event)"
  >
    <slot></slot>
  </v-form>
</template>

<script setup lang="ts">
import { getScrollParent } from "@/app/ui/utilities";
import { getCurrentInstance, inject, onUnmounted, provide, ref } from "vue";
import { useVuetify } from "@/plugins/vuetify";
import type { Validator } from "@/components/platform/hybrid/forms";
import {
  registerValidatorKey,
  unregisterValidatorKey,
} from "@/components/platform/hybrid/forms";
import Guid from "@/app/types/common/guid";

/**
 * Keeps track of all controls in a form and scrolls to the first validation error on validate.
 * Use this as a replacement for v-form, not in conjunction.
 */

interface Props {
  value?: boolean;
  lazyValidation?: boolean;
  preventSubmit?: boolean;
}

interface Events {
  (event: "update:value", value: boolean): void;
  (event: "submit", e: Event): void;
}

const props = withDefaults(defineProps<Props>(), {
  lazyValidation: false,
  preventSubmit: false,
});

const emit = defineEmits<Events>();

const formId = Guid.create();
const vFormRef = ref<any>(null);
const { $vuetify } = useVuetify();

const instance = getCurrentInstance();
provide("form", instance);

const registerValidator = inject(registerValidatorKey, undefined);
registerValidator?.(formId, instance?.proxy as unknown as Validator);
const unregisterValidator = inject(unregisterValidatorKey, undefined);

/** public */
const focus = () => {
  // Breaks v-form encapsulation
  if (
    vFormRef.value.inputs.length > 0 &&
    typeof vFormRef.value.inputs[0].focus === "function"
  ) {
    // Seem to need to allow the browser to re-calculate focusable elements prior to changing focus
    setTimeout(() => vFormRef.value.inputs[0].focus(), 10);
  }
};

const validate = (): boolean => {
  // Breaks encapsulation and accesses the inner details of VForm. Likely to break if VForm implementation changes.
  const errorInputs = vFormRef.value.inputs.filter(
    (input: any) => !input.validate(true)
  );

  // scrolls to first failing element
  if (errorInputs.length) {
    // This doesn't seem to work with child
    $vuetify
      .goTo(errorInputs[0], {
        offset: 100,
        container: getScrollParent(errorInputs[0].$el, false),
      })
      .then();
  }

  return !errorInputs.length;
};

const reset = () => {
  vFormRef.value.reset();
};

const resetValidation = () => {
  vFormRef.value.resetValidation();
};

const submit = (event: Event) => {
  if (props.preventSubmit) {
    event.preventDefault();
  } else {
    emit("submit", event);
  }
};

onUnmounted(() => {
  unregisterValidator?.(formId);
});

defineExpose({
  focus,
  validate,
  reset,
  resetValidation,
});
</script>
